import React, { useState, useEffect } from 'react';
import {
  Grid,
  CircularProgress,
  TextField as Input,
  Typography,
  Box,
  Paper,
  Container,
  Fade,
  Avatar,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
  Button as MuiButton,
} from '@mui/material';

import { withRouter } from 'react-router-dom';
import { LockOutlined, EmailOutlined, Visibility, VisibilityOff, VpnKeyOutlined, CheckCircle, PhoneOutlined } from '@mui/icons-material';

// styles
import useStyles from './styles';

// context
import { useUserDispatch, receiveToken, doInit } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';

//components
import { Button } from '../../components/Wrappers';

// branding
import { BRANDING } from '../../utils/branding';
const logo = BRANDING.logo;

const getGreeting = () => {
  const d = new Date();
  if (d.getHours() >= 4 && d.getHours() <= 12) {
    return 'Good Morning';
  } else if (d.getHours() >= 13 && d.getHours() <= 16) {
    return 'Good Day';
  } else if (d.getHours() >= 17 && d.getHours() <= 23) {
    return 'Good Evening';
  } else {
    return 'Good Night';
  }
};

function Login(props) {
  let classes = useStyles();

  // global
  let userDispatch = useUserDispatch();
  const auth = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const token = params.get('token');

    if (token) {
      receiveToken(token, userDispatch, props.history);
      doInit()(userDispatch);
    }
  }, []); // eslint-disable-line

  // local
  let [isLoading, setIsLoading] = useState(false);
  let [error, setError] = useState(null);
  let [loginValue, setLoginValue] = useState('');
  let [passwordValue, setPasswordValue] = useState('');
  let [showPassword, setShowPassword] = useState(false);
  let [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  let [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Verify OTP, 2: Set New Password
  let [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  let [forgotPasswordOtp, setForgotPasswordOtp] = useState('');
  let [newPassword, setNewPassword] = useState('');
  let [confirmPassword, setConfirmPassword] = useState('');
  let [showNewPassword, setShowNewPassword] = useState(false);
  let [showConfirmPassword, setShowConfirmPassword] = useState(false);
  let [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  let [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  let [forgotPasswordError, setForgotPasswordError] = useState(null);
  let [resendOtpLoading, setResendOtpLoading] = useState(false);
  let [resendOtpCooldown, setResendOtpCooldown] = useState(0);
  let [setPasswordOpen, setSetPasswordOpen] = useState(false);
  let [setPasswordStep, setSetPasswordStep] = useState(1); // 1: Request OTP, 2: Enter OTP
  let [setPasswordEmail, setSetPasswordEmail] = useState('');
  let [setPasswordOtp, setSetPasswordOtp] = useState('');
  let [setPasswordNewPassword, setSetPasswordNewPassword] = useState('');
  let [setPasswordConfirmPassword, setSetPasswordConfirmPassword] = useState('');
  let [showSetPasswordNewPassword, setShowSetPasswordNewPassword] = useState(false);
  let [showSetPasswordConfirmPassword, setShowSetPasswordConfirmPassword] = useState(false);
  let [setPasswordLoading, setSetPasswordLoading] = useState(false);
  let [setPasswordSuccess, setSetPasswordSuccess] = useState(false);
  let [setPasswordError, setSetPasswordError] = useState(null);
  let [resendSetPasswordOtpLoading, setResendSetPasswordOtpLoading] = useState(false);
  let [resendSetPasswordOtpCooldown, setResendSetPasswordOtpCooldown] = useState(0);
  let [setPasswordRequestId, setSetPasswordRequestId] = useState('');
  let [setPasswordAccessToken, setSetPasswordAccessToken] = useState('');

  // Countdown timer for resend OTP
  useEffect(() => {
    let interval = null;
    if (resendOtpCooldown > 0) {
      interval = setInterval(() => {
        setResendOtpCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendOtpCooldown]);

  // Countdown timer for resend Set Password OTP
  useEffect(() => {
    let interval = null;
    if (resendSetPasswordOtpCooldown > 0) {
      interval = setInterval(() => {
        setResendSetPasswordOtpCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendSetPasswordOtpCooldown]);

  const handleLogin = async () =>
    handleLoginInner({
      auth,
      userDispatch,
      loginValue,
      passwordValue,
      history: props.history,
      setIsLoading,
      setError,
    });

  let isLoginFormValid = () => {
    return loginValue.length !== 0 && passwordValue.length !== 0;
  };

  let loginOnEnterKey = async (event) => {
    if (event.key === 'Enter' && isLoginFormValid()) {
      await handleLogin();
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordOpen(true);
    setForgotPasswordStep(1);
    setForgotPasswordPhone('');
    setForgotPasswordOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError(null);
    setResendOtpLoading(false);
    setResendOtpCooldown(0);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordStep(1);
    setForgotPasswordPhone('');
    setForgotPasswordOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError(null);
    setResendOtpLoading(false);
    setResendOtpCooldown(0);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setForgotPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotPasswordError('The new password and confirmation password must match. Please try again.');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError(null);
    setForgotPasswordSuccess(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/change-password/forgot-password-verify-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: forgotPasswordPhone,
          otp: forgotPasswordOtp,
          newPassword: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to reset password');
      }

      setForgotPasswordSuccess(true);
      setTimeout(() => {
        handleCloseForgotPassword();
      }, 2000);
    } catch (err) {
      setForgotPasswordError(err?.message || 'Failed to reset password. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!forgotPasswordPhone || forgotPasswordPhone.length < 10) {
      setForgotPasswordError('Please enter a valid phone number first');
      return;
    }

    if (resendOtpCooldown > 0) {
      return;
    }

    setResendOtpLoading(true);
    setForgotPasswordError(null);
    setForgotPasswordOtp('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/change-password/forgot-password-send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: forgotPasswordPhone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to send OTP');
      }

      // Set cooldown to 60 seconds
      setResendOtpCooldown(60);
      if (forgotPasswordStep === 1) {
        setForgotPasswordStep(2);
      }
      setForgotPasswordError(null);
    } catch (err) {
      setForgotPasswordError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setResendOtpLoading(false);
    }
  };

  const handleOpenSetPassword = () => {
    setSetPasswordOpen(true);
    setSetPasswordStep(1);
    setSetPasswordEmail('');
    setSetPasswordOtp('');
    setSetPasswordNewPassword('');
    setSetPasswordConfirmPassword('');
    setShowSetPasswordNewPassword(false);
    setShowSetPasswordConfirmPassword(false);
    setSetPasswordSuccess(false);
    setSetPasswordError(null);
    setResendSetPasswordOtpLoading(false);
    setResendSetPasswordOtpCooldown(0);
  };

  const handleCloseSetPassword = () => {
    setSetPasswordOpen(false);
    setSetPasswordStep(1);
    setSetPasswordEmail('');
    setSetPasswordOtp('');
    setSetPasswordNewPassword('');
    setSetPasswordConfirmPassword('');
    setShowSetPasswordNewPassword(false);
    setShowSetPasswordConfirmPassword(false);
    setSetPasswordSuccess(false);
    setSetPasswordError(null);
    setResendSetPasswordOtpLoading(false);
    setResendSetPasswordOtpCooldown(0);
    setSetPasswordRequestId('');
    setSetPasswordAccessToken('');
  };

  const handleVerifySetPasswordOtp = async () => {
    if (setPasswordStep === 1) {
      if (!setPasswordEmail || !setPasswordEmail.includes('@')) {
        setSetPasswordError('Please enter a valid email address');
        return;
      }
      setSetPasswordLoading(true);
      setSetPasswordError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/setPassword`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: setPasswordEmail }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.status === false) {
          throw new Error(data?.message || 'Failed to send OTP');
        }
        setSetPasswordRequestId(data?.data || '');
        setSetPasswordStep(2);
      } catch (err) {
        setSetPasswordError(err?.message || 'Failed to send OTP. Please try again.');
      } finally {
        setSetPasswordLoading(false);
      }
      return;
    }

    if (setPasswordStep === 2) {
      if (!setPasswordRequestId) {
        setSetPasswordError('Please send OTP first.');
        return;
      }
      if (!setPasswordOtp || setPasswordOtp.length < 4) {
        setSetPasswordError('Please enter a valid OTP');
        return;
      }
      setSetPasswordLoading(true);
      setSetPasswordError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/verifySetPasswordOtp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            otp: setPasswordOtp,
            key: setPasswordRequestId,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.status === false) {
          throw new Error(data?.message || 'Invalid OTP. Please try again.');
        }
        const tokenFromOtp = data?.data?.token || data?.data?.accessToken || '';
        if (tokenFromOtp) {
          setSetPasswordAccessToken(tokenFromOtp);
        }
        setSetPasswordStep(3);
      } catch (err) {
        setSetPasswordError(err?.message || 'Invalid OTP. Please try again.');
      } finally {
        setSetPasswordLoading(false);
      }
    }
  };

  const handleSetPassword = async () => {
    if (!setPasswordNewPassword || setPasswordNewPassword.length < 6) {
      setSetPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (setPasswordNewPassword !== setPasswordConfirmPassword) {
      setSetPasswordError('The new password and confirmation password must match. Please try again.');
      return;
    }

    setSetPasswordLoading(true);
    setSetPasswordError(null);
    setSetPasswordSuccess(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/resetPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(setPasswordAccessToken ? { Authorization: `Bearer ${setPasswordAccessToken}` } : {}),
        },
        body: JSON.stringify({
          password: setPasswordNewPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to set password');
      }

      setSetPasswordSuccess(true);
      setTimeout(() => {
        handleCloseSetPassword();
        window.alert('Password Changed Successfully');
        if (props?.history?.push) {
          props.history.push('/login');
        }
      }, 1500);
    } catch (err) {
      setSetPasswordError(err?.message || 'Failed to set password. Please try again.');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleResendSetPasswordOtp = async () => {
    if (!setPasswordEmail || !setPasswordEmail.includes('@')) {
      setSetPasswordError('Please enter a valid email address first');
      return;
    }

    if (resendSetPasswordOtpCooldown > 0) {
      return;
    }

    setResendSetPasswordOtpLoading(true);
    setSetPasswordError(null);
    setSetPasswordOtp('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/setPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: setPasswordEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to resend OTP');
      }
      setSetPasswordRequestId(data?.data || '');
      setResendSetPasswordOtpCooldown(60);
      setSetPasswordError(null);
    } catch (err) {
      setSetPasswordError(err?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendSetPasswordOtpLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#090d16',
        color: '#f8fafc',
        fontFamily: '"Inter", "Outfit", sans-serif',
      }}
    >
      {/* Global CSS for Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) scale(1) rotate(0deg); }
          50% { transform: translateY(-30px) scale(1.15) rotate(180deg); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0px) scale(1) rotate(0deg); }
          50% { transform: translateY(25px) scale(0.9) rotate(-180deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; filter: drop-shadow(0 0 15px rgba(99, 102, 241, 0.4)); }
          50% { opacity: 0.6; filter: drop-shadow(0 0 30px rgba(99, 102, 241, 0.8)); }
        }
        @keyframes pulse-cyan {
          0%, 100% { opacity: 0.4; filter: drop-shadow(0 0 10px rgba(6, 182, 212, 0.4)); }
          50% { opacity: 0.8; filter: drop-shadow(0 0 25px rgba(6, 182, 212, 0.8)); }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes cyber-shine {
          0% { left: -100%; }
          50% { left: 100%; }
          100% { left: 100%; }
        }
        @keyframes data-stream {
          0% { height: 0%; opacity: 0; }
          50% { height: 100%; opacity: 0.8; }
          100% { height: 100%; opacity: 0; }
        }
        @keyframes float-card {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}} />

      {/* Cybernetic Tech Grid and Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '45px 45px',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Futuristic Glowing Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '10%',
          width: '50vw',
          height: '50vw',
          maxHeight: '600px',
          maxWidth: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0) 70%)',
          filter: 'blur(80px)',
          animation: 'float-slow 20s infinite ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          right: '5%',
          width: '45vw',
          height: '45vw',
          maxHeight: '550px',
          maxWidth: '550px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0) 70%)',
          filter: 'blur(70px)',
          animation: 'float-medium 15s infinite ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          right: '35%',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0) 60%)',
          filter: 'blur(60px)',
          animation: 'float-slow 12s infinite ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Main Container Split Screen Layout */}
      <Grid container sx={{ minHeight: '100vh', zIndex: 2, position: 'relative' }}>
        {/* Left Pane - CRM Technology Visualizer (Hidden on Mobile) */}
        <Grid
          item
          xs={0}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 6,
            borderRight: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(135deg, rgba(8, 12, 21, 0.6) 0%, rgba(15, 23, 42, 0.4) 100%)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
          }}
        >
          {/* Subtle light effect down the divider */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: '10%',
              bottom: '10%',
              width: '1px',
              background: 'linear-gradient(to bottom, transparent, rgba(6, 182, 212, 0.4), transparent)',
            }}
          />

          <Box sx={{ maxWidth: '480px', width: '100%' }}>
            {/* Visualizer Badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                background: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                borderRadius: '99px',
                px: 2,
                py: 0.8,
                mb: 4,
                backdropFilter: 'blur(5px)',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#6366f1',
                  animation: 'pulse-glow 2s infinite ease-in-out',
                }}
              />
              <Typography variant="caption" sx={{ color: '#818cf8', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Next-Gen CRM Engine
              </Typography>
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 2,
                background: 'linear-gradient(135deg, #ffffff 30%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.03em',
              }}
            >
              Powering Your Customer Relationships
            </Typography>

            <Typography variant="body1" sx={{ color: '#94a3b8', mb: 5, lineHeight: 1.6 }}>
              Seamless lead management, real-time telemetry, and advanced analytics unified in one singular workspace.
            </Typography>

            {/* Simulated Live Tech CRM Panel */}
            <Box
              sx={{
                background: 'rgba(15, 23, 42, 0.65)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: 4,
                p: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'float-card 6s infinite ease-in-out',
              }}
            >
              {/* Scanline card overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(to right, transparent, rgba(6, 182, 212, 0.5), transparent)',
                  animation: 'scanline 4s infinite linear',
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                  <Typography variant="subtitle2" sx={{ color: '#e2e8f0', fontWeight: 600 }}>System Telemetry</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>active_node_01</Typography>
              </Box>

              {/* CRM Metrics grid */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>LEADS ACQUIRED</Typography>
                    <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>4,921</Typography>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>+12.4% today</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>CONVERSION RATE</Typography>
                    <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>24.8%</Typography>
                    <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600 }}>Optimal</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Graphic Chart Simulation */}
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: 80, gap: 1 }}>
                  {[35, 60, 45, 80, 50, 75, 90, 65, 85, 100].map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        bgcolor: i === 6 || i === 9 ? '#06b6d4' : 'rgba(99, 102, 241, 0.4)',
                        borderRadius: '3px 3px 0 0',
                        height: `${h}%`,
                        transition: 'height 1s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '20%',
                          background: 'rgba(255,255,255,0.3)',
                        }
                      }}
                    />
                  ))}
                </Box>
                <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', mt: 1 }} />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Right Pane - Sleek Login Card Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="xs" sx={{ p: 0 }}>
            <Fade in timeout={1000}>
              <Box
                sx={{
                  background: 'rgba(15, 23, 42, 0.55)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 5,
                  p: { xs: 3, sm: 5 },
                  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Visual Accent Top Line */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(to right, #6366f1, #06b6d4, #8b5cf6)',
                  }}
                />

                {/* Logo & Header */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -5,
                        left: -5,
                        right: -5,
                        bottom: -5,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
                        animation: 'pulse-cyan 3s infinite ease-in-out',
                      }}
                    />
                    <Box
                      component="img"
                      src={logo}
                      alt={`${BRANDING.name} Logo`}
                      sx={{
                        width: { xs: 65, sm: 75 },
                        height: 'auto',
                        filter: 'drop-shadow(0 4px 15px rgba(6, 182, 212, 0.2))',
                        position: 'relative',
                        zIndex: 2,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#ffffff',
                      textAlign: 'center',
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      letterSpacing: '-0.02em',
                      background: 'linear-gradient(135deg, #ffffff 60%, #a5b4fc 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {BRANDING.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                      textAlign: 'center',
                      mt: 1,
                      fontWeight: 500,
                    }}
                  >
                    Sign in to your tech console
                  </Typography>
                </Box>

                {/* Error Message */}
                {error && (
                  <Fade in>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: 2,
                        py: 1.5,
                        px: 2,
                        mb: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f87171',
                          textAlign: 'center',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                        }}
                      >
                        {typeof error === 'string' ? error : 'Authentication failed. Please verify credentials.'}
                      </Typography>
                    </Box>
                  </Fade>
                )}

                {/* Form Fields */}
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Input
                    id="email"
                    value={loginValue}
                    onChange={(e) => setLoginValue(e.target.value)}
                    label="Login ID"
                    placeholder="Enter your login ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onKeyDown={(e) => loginOnEnterKey(e)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined sx={{ color: '#6366f1' }} />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      sx: {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: '#6366f1',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        color: '#f8fafc',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(99, 102, 241, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '1.5px',
                          boxShadow: '0 0 15px rgba(99, 102, 241, 0.25)',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.95rem',
                        '&::placeholder': {
                          color: '#475569',
                          opacity: 1,
                        },
                      },
                    }}
                  />

                  <Input
                    id="password"
                    value={passwordValue}
                    onChange={(e) => setPasswordValue(e.target.value)}
                    label="Password"
                    placeholder="Enter your password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    onKeyDown={(e) => loginOnEnterKey(e)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ color: '#06b6d4' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ color: '#64748b', '&:hover': { color: '#f8fafc' } }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      sx: {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: '#06b6d4',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        color: '#f8fafc',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(6, 182, 212, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#06b6d4',
                          borderWidth: '1.5px',
                          boxShadow: '0 0 15px rgba(6, 182, 212, 0.25)',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.95rem',
                        '&::placeholder': {
                          color: '#475569',
                          opacity: 1,
                        },
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleOpenForgotPassword}
                      sx={{
                        color: '#818cf8',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#a5b4fc',
                          textDecoration: 'none',
                          filter: 'drop-shadow(0 0 5px rgba(129,140,248,0.5))',
                        },
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                </Box>

                {/* Login Button */}
                <Box sx={{ mb: 3 }}>
                  <Button
                    disabled={isLoading || !isLoginFormValid()}
                    onClick={handleLogin}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      borderRadius: 3,
                      py: 1.6,
                      fontSize: '0.98rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                      color: '#ffffff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      border: 'none',
                      boxShadow: '0 8px 25px rgba(99, 102, 241, 0.35)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                        animation: 'cyber-shine 4s infinite ease-in-out',
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                        boxShadow: '0 12px 30px rgba(99, 102, 241, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(1px)',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        boxShadow: 'none',
                        color: 'rgba(255, 255, 255, 0.2)',
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }} />
                    ) : null}
                    <span style={{ color: isLoading || !isLoginFormValid() ? 'rgba(255, 255, 255, 0.25)' : '#ffffff' }}>
                      {isLoading ? 'Establishing Session…' : 'Access Dashboard'}
                    </span>
                  </Button>
                </Box>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      letterSpacing: '0.05em',
                    }}
                  >
                    © {new Date().getFullYear()} {BRANDING.name}. SECURE TELEMETRY LINK.
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Grid>
      </Grid>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(25px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
            color: '#f8fafc',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
            Security Key Recovery
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {forgotPasswordSuccess ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, textAlign: 'center' }}>
              <Box sx={{ mb: 2, animation: 'scaleIn 0.5s ease-in-out' }}>
                <CheckCircle sx={{ fontSize: 70, color: '#10b981' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                Configuration Complete
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Your security profile has been updated. Initializing redirection...
              </Typography>
            </Box>
          ) : forgotPasswordStep === 1 ? (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: '#94a3b8' }}>
                Verify phone record to request temporary verification code (OTP).
              </Typography>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                  {forgotPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="forgot-phone"
                label="Registered Mobile Number"
                type="tel"
                fullWidth
                value={forgotPasswordPhone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setForgotPasswordPhone(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !resendOtpLoading) {
                    handleResendOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <PhoneOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: '#94a3b8' }}>
                Enter OTP code and configure new access key.
              </Typography>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                  {forgotPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="forgot-otp"
                label="One-Time Passcode"
                type="text"
                fullWidth
                value={forgotPasswordOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setForgotPasswordOtp(value);
                }}
                InputProps={{
                  startAdornment: (
                    <VpnKeyOutlined sx={{ color: '#06b6d4', mr: 1 }} />
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#06b6d4' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#06b6d4' },
                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' },
                  }
                }}
                placeholder="6-digit code"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
                {resendOtpCooldown > 0 ? (
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                    Request reset in {resendOtpCooldown}s
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendOtp}
                    disabled={resendOtpLoading}
                    sx={{
                      color: '#06b6d4',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {resendOtpLoading ? 'Transmitting...' : 'Request Code Resend'}
                  </Link>
                )}
              </Box>

              <Input
                margin="dense"
                id="new-password"
                label="New Console Key"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !forgotPasswordLoading) {
                    handleResetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: '#64748b' }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
              <Input
                margin="dense"
                id="confirm-password"
                label="Confirm Key"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !forgotPasswordLoading) {
                    handleResetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#64748b' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          {!forgotPasswordSuccess && (
            <>
              <MuiButton
                onClick={forgotPasswordStep === 2 ? () => setForgotPasswordStep(1) : handleCloseForgotPassword}
                sx={{ color: '#94a3b8', textTransform: 'none', fontWeight: 600 }}
              >
                {forgotPasswordStep === 2 ? 'Back' : 'Abort'}
              </MuiButton>
              {forgotPasswordStep === 1 ? (
                <MuiButton
                  onClick={handleResendOtp}
                  disabled={resendOtpLoading || !forgotPasswordPhone}
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    px: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#4f46e5' },
                  }}
                >
                  {resendOtpLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send Code'}
                </MuiButton>
              ) : (
                <MuiButton
                  onClick={handleResetPassword}
                  disabled={forgotPasswordLoading || !newPassword || !confirmPassword}
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    px: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#4f46e5' },
                  }}
                >
                  {forgotPasswordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Commit Reset'}
                </MuiButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog
        open={setPasswordOpen}
        onClose={handleCloseSetPassword}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(25px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
            color: '#f8fafc',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
            Set Account Key
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {setPasswordSuccess ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, textAlign: 'center' }}>
              <Box sx={{ mb: 2, animation: 'scaleIn 0.5s ease-in-out' }}>
                <CheckCircle sx={{ fontSize: 70, color: '#10b981' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                Configuration Complete
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Your security key has been initialized. Proceeding...
              </Typography>
            </Box>
          ) : setPasswordStep === 1 ? (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: '#94a3b8' }}>
                Verify registered email to request security code.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-password-email"
                label="Registered Email"
                type="email"
                fullWidth
                value={setPasswordEmail}
                onChange={(e) => setSetPasswordEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleVerifySetPasswordOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <EmailOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
            </>
          ) : setPasswordStep === 2 ? (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: '#94a3b8' }}>
                Provide verification code received via email registry.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-password-otp"
                label="Verification Code"
                type="text"
                fullWidth
                value={setPasswordOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setSetPasswordOtp(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleVerifySetPasswordOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <VpnKeyOutlined sx={{ color: '#06b6d4', mr: 1 }} />
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#06b6d4' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#06b6d4' },
                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' },
                  }
                }}
                placeholder="Enter Code"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
                {resendSetPasswordOtpCooldown > 0 ? (
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                    Request retry in {resendSetPasswordOtpCooldown}s
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendSetPasswordOtp}
                    disabled={resendSetPasswordOtpLoading || !setPasswordEmail}
                    sx={{
                      color: '#06b6d4',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {resendSetPasswordOtpLoading ? 'Transmitting...' : 'Request Code Resend'}
                  </Link>
                )}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3, color: '#94a3b8' }}>
                Set new console password key.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171' }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-new-password"
                label="New Password Key"
                type={showSetPasswordNewPassword ? 'text' : 'password'}
                fullWidth
                value={setPasswordNewPassword}
                onChange={(e) => setSetPasswordNewPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleSetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSetPasswordNewPassword(!showSetPasswordNewPassword)}
                        edge="end"
                        sx={{ color: '#64748b' }}
                      >
                        {showSetPasswordNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
              <Input
                margin="dense"
                id="set-confirm-password"
                label="Confirm Password Key"
                type={showSetPasswordConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={setPasswordConfirmPassword}
                onChange={(e) => setSetPasswordConfirmPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleSetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#6366f1', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSetPasswordConfirmPassword(!showSetPasswordConfirmPassword)}
                        edge="end"
                        sx={{ color: '#64748b' }}
                      >
                        {showSetPasswordConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ sx: { color: '#64748b', '&.Mui-focused': { color: '#6366f1' } } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          {!setPasswordSuccess && (
            <>
              <MuiButton
                onClick={setPasswordStep === 1 ? handleCloseSetPassword : () => setSetPasswordStep(setPasswordStep - 1)}
                sx={{ color: '#94a3b8', textTransform: 'none', fontWeight: 600 }}
              >
                {setPasswordStep === 1 ? 'Abort' : 'Back'}
              </MuiButton>
              {setPasswordStep === 1 ? (
                <MuiButton
                  onClick={handleVerifySetPasswordOtp}
                  disabled={setPasswordLoading || !setPasswordEmail}
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    px: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#4f46e5' },
                  }}
                >
                  {setPasswordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send Code'}
                </MuiButton>
              ) : setPasswordStep === 2 ? (
                <MuiButton
                  onClick={handleVerifySetPasswordOtp}
                  disabled={setPasswordLoading || !setPasswordOtp}
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    px: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#4f46e5' },
                  }}
                >
                  {setPasswordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Verify Code'}
                </MuiButton>
              ) : (
                <MuiButton
                  onClick={handleSetPassword}
                  disabled={setPasswordLoading || !setPasswordNewPassword || !setPasswordConfirmPassword}
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    px: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { background: '#4f46e5' },
                  }}
                >
                  {setPasswordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Commit Password'}
                </MuiButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// New login handler using AuthContext and /api/login
async function handleLoginInner({
  auth,
  userDispatch,
  loginValue,
  passwordValue,
  history,
  setIsLoading,
  setError,
}) {
  try {
    setError(null);
    setIsLoading(true);
    const token = await auth.login({ loginId: loginValue, password: passwordValue });
    try {
      const claims = auth.user || null;
      if (claims) {
        localStorage.setItem('user', JSON.stringify(claims));
      }
    } catch { }
    // Keep existing UserContext expectations: store token and init
    // receiveToken(token, userDispatch, history);
    // doInit()(userDispatch);
    receiveToken(token, userDispatch); // NO history here
    doInit()(userDispatch);
    // wait for auth state → then navigate
    setTimeout(() => {
      history.push("/dashboard");
    }, 0);

  } catch (e) {
    console.log(e);
    // Extract error message from API response
    let errorMessage = 'Something is wrong with your login or password.';

    if (e?.response?.data?.message) {
      errorMessage = e.response.data.message;
    } else if (e?.message) {
      errorMessage = e.message;
    }

    setError(errorMessage);
  } finally {
    setIsLoading(false);
  }
}

export default withRouter(Login);
